import { type MenuInputOption } from '@saviynt/design-system';

import type {
  DataProvider,
  DefaultValue,
  FormValues,
} from '../../../shared/types';

import { evaluateExpressionValue } from '../../ExpressionBuilder/utils';
import { type EntityInitialData } from '../data-store/initialDataSlice';

/**
 * Internal normalized representation of a default value, unifying the new
 * `selected` array format and the legacy plain-string format into a single shape.
 */
export interface ResolvedDefault {
  entries: Array<{ value: string; label: string }>;
  isExpression: boolean;
}

/**
 * Normalizes a `DefaultValue` into a `ResolvedDefault` with concrete entries.
 *
 * Resolution rules:
 * 1. If `isExpression` is true → evaluate the expression; result becomes
 *    `[{ value: result, label: result }]`. Empty/null results yield an empty array.
 * 2. If `selected` exists and has entries → use them directly.
 * 3. If `selected` is absent (legacy format) → derive from `value`:
 *    - singleselect: `[{ value: val, label: val }]`
 *    - multiselect: split by comma into `[{ value: v, label: v }, ...]`
 * 4. Any entry with an empty `label` gets its `value` copied into `label`.
 */
export function normalizeDefaultValue(
  defaultValue: DefaultValue | undefined,
  fieldType: string,
  formValues: FormValues,
  initialData: EntityInitialData | null
): ResolvedDefault {
  const empty: ResolvedDefault = { entries: [], isExpression: false };

  if (!defaultValue) {
    return empty;
  }

  // Expression path: evaluate first, then normalize the result
  if (defaultValue.isExpression) {
    if (!defaultValue.value) {
      return { entries: [], isExpression: true };
    }

    const evaluated = evaluateExpressionValue(defaultValue.value, {
      values: formValues,
      touched: {},
      entityData: initialData,
    });

    if (!evaluated) {
      return { entries: [], isExpression: true };
    }

    // Expression result is a value code; label is empty so the fallback below fills it
    return {
      entries: [{ value: evaluated, label: evaluated }],
      isExpression: true,
    };
  }

  // New format: `selected` array is present and has entries
  if (defaultValue.selected && defaultValue.selected.length > 0) {
    const entries = defaultValue.selected
      .filter((entry) => entry.value)
      .map((entry) => ({
        value: entry.value,
        label: entry.label || entry.value,
      }));

    return { entries, isExpression: false };
  }

  // Legacy format: derive entries from the plain `value` string
  if (!defaultValue.value) {
    return empty;
  }

  if (fieldType === 'multiselect') {
    const entries = defaultValue.value
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v)
      .map((v) => ({ value: v, label: v }));

    return { entries, isExpression: false };
  }

  // Singleselect (or any other dropdown type)
  return {
    entries: [{ value: defaultValue.value, label: defaultValue.value }],
    isExpression: false,
  };
}

/**
 * Prepends synthetic options for default values not found in the loaded options.
 *
 * - Fixed provider ('FIXED'): never injects — returns `loadedOptions` unchanged.
 * - HTTP / SQL / SavOQ providers: for each default whose `value` is not already
 *   present in `loadedOptions`, a synthetic `MenuInputOption` is prepended.
 * - Entries with an empty `value` string are skipped.
 * - When a default entry has an empty `label`, the `value` is used as the label.
 *
 * @param loadedOptions - Options already fetched from the data provider.
 * @param defaults      - Default value entries to potentially inject.
 * @param providerType  - The data provider type driving injection behavior.
 * @returns The augmented options array with any injected entries prepended.
 */
export function injectDefaultOptions(
  loadedOptions: MenuInputOption[],
  defaults: Array<{ value: string; label: string }>,
  providerType: DataProvider['type']
): MenuInputOption[] {
  // Fixed providers resolve by matching only — never inject synthetic options
  if (providerType === 'FIXED') {
    return loadedOptions;
  }

  const existingValues = new Set(loadedOptions.map((opt) => String(opt.value)));

  const syntheticOptions: MenuInputOption[] = defaults
    .filter((entry) => entry.value && !existingValues.has(entry.value))
    .map((entry) => ({
      value: entry.value,
      label: entry.label || entry.value,
      isChecked: true,
    }));

  return [...syntheticOptions, ...loadedOptions];
}
